import { useCallback, useEffect, useState } from 'react';

// Stored in `window.name`, which belongs to the tab and survives full-page
// navigations between our platforms (same site, different subdomains/ports),
// unlike sessionStorage which is per-origin. A new tab starts with it empty.
const TAB_MARKER = 'datizy-logo-loader-shown';

const isShownInThisTab = (): boolean => {
  try {
    return window.name.split(' ').includes(TAB_MARKER);
  } catch {
    return false;
  }
};

const markShownInThisTab = () => {
  try {
    if (isShownInThisTab()) return;
    window.name = window.name ? `${window.name} ${TAB_MARKER}` : TAB_MARKER;
  } catch {
    // ignore
  }
};

// A reload reports `reload`; switching apps (auth -> admin/user,
// admin <-> user) is a regular `navigate` in the same tab.
const isPageReload = (): boolean => {
  try {
    const [entry] = performance.getEntriesByType(
      'navigation',
    ) as PerformanceNavigationTiming[];
    return entry?.type === 'reload';
  } catch {
    return false;
  }
};

/** Lets the intro play again in this tab, e.g. after logout. */
export const resetLogoLoaderForTab = () => {
  try {
    window.name = window.name
      .split(' ')
      .filter((part) => part !== TAB_MARKER)
      .join(' ');
  } catch {
    // ignore
  }
};

/**
 * Decides whether the intro LogoLoader should be rendered: on the first load
 * of a tab and on page reload, but not when navigating between our apps in
 * the same tab. Never in local development, and never in a tab opened in the
 * background (browsers defer video playback there until the tab is focused).
 */
export default function useLogoLoader(enabled: boolean) {
  const [eligible] = useState(
    () =>
      !import.meta.env.DEV &&
      document.visibilityState === 'visible' &&
      (isPageReload() || !isShownInThisTab()),
  );
  const [finished, setFinished] = useState(false);

  const visible = enabled && eligible && !finished;

  useEffect(() => {
    if (visible) markShownInThisTab();
  }, [visible]);

  const hide = useCallback(() => setFinished(true), []);

  return { visible, hide };
}
